import React, { useEffect, useRef } from 'react';
import { Flex, Image } from 'antd';
import { getChildList, getQueryParamsForCategoryModal, getSubChildList } from '../helper';
import { Group } from '@common-web';
import { FaChevronRight } from 'react-icons/fa6';
import cx from 'clsx';
import styles from './index.module.scss';
import InfiniteScroll from 'react-infinite-scroll-component';
import { categoriesApi } from '@apis/api-helper';
import { Input, Skeleton } from '../../../../common/web-view/index.js';
import { useTranslation } from 'react-i18next';
import CategoryLoader from './category-loader';
import placeholderImage from '../../../../assets/placeholder.png';

const CategoryModalBody = ({
  list = [],
  setList,
  selected = null,
  loading = {},
  pagination = null,
  setPagination,
  paginationChild = null,
  setPaginationChild,
  searchQuery,
  childSearchQuery,
  onInputChange,
  onChildInputChange,
  paginationSubChild = null,
  setPaginationSubChild,
  onSubChildInputChange,
  subChildSearchQuery,
  handleItemClick,
}) => {
  const { t } = useTranslation();

  const subChildList = getSubChildList(list) || [];

  const parentSelectedRef = useRef(null);
  const childSelectedRef = useRef(null);
  const subChildSelectedRef = useRef(null);

  useEffect(() => {
    if (parentSelectedRef?.current) parentSelectedRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (childSelectedRef?.current) childSelectedRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (subChildSelectedRef?.current)
      subChildSelectedRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [loading]);

  return (
    <>
      <Group template={'repeat(3, 1fr)'} gap={'0'}>
        <div className={styles.categoryList}>
          <Input
            placeholder={t('Search')}
            value={searchQuery}
            autoFocus={true}
            onChange={onInputChange}
            containerClassName={cx('flexGrow', styles.categoryInput)}
          />
          <InfiniteScroll
            hasMore={!!pagination?.next_page}
            next={async () => {
              if (!!pagination?.next_page) {
                const res = await categoriesApi(
                  getQueryParamsForCategoryModal(null, pagination?.next_page, searchQuery)
                );
                setList([
                  ...list,
                  ...res?.categories?.map((item) => ({
                    ...item,
                    child: list.find((category) => category?.id === item?.id)?.child,
                  })),
                ]);
                setPagination(res?.pagination);
              }
            }}
            loader={<CategoryLoader />}
            height={400}
            className={styles.scrollY}
            dataLength={list?.length}
          >
            {loading?.parent ? (
              <CategoryLoader />
            ) : (
              list?.map((item) => (
                <Flex
                  align="center"
                  justify="space-between"
                  className={cx(styles.category, selected?.parent?.id === item.id ? styles.selected : '')}
                  onClick={() => handleItemClick(item, 'parent')}
                  key={item.id}
                  {...(selected?.parent?.id === item.id ? { ref: parentSelectedRef } : {})}
                >
                  <Flex align="center" gap={16}>
                    {
                      <Image
                        src={item?.icon?.sizes?.document ?? placeholderImage}
                        preview={false}
                        width={32}
                        height={32}
                        placeholder={<Skeleton width={32} height={32} borderRadius={4} />}
                      />
                    }
                    <div className={styles.categoryName}>{item?.name}</div>
                  </Flex>
                  <FaChevronRight size={12} />
                </Flex>
              ))
            )}
          </InfiniteScroll>
        </div>
        <div className={styles.categoryList}>
          {selected?.parent?.id && (
            <Input
              placeholder={t('Search')}
              value={childSearchQuery}
              autoFocus={true}
              onChange={onChildInputChange}
              containerClassName={cx('flexGrow', styles.categoryInput)}
            />
          )}
          <InfiniteScroll
            hasMore={!!paginationChild?.next_page}
            next={async () => {
              if (!!paginationChild?.next_page) {
                const res = await categoriesApi(
                  getQueryParamsForCategoryModal(selected, paginationChild?.next_page, childSearchQuery)
                );
                const newList = [...list];
                const index = newList?.findIndex((item) => res?.categories[0]?.parent_id === item?.id);
                newList[index].child = [...newList[index]?.child, ...res?.categories];
                setPaginationChild(res?.pagination);
              }
            }}
            loader={!selected?.child && <CategoryLoader />}
            height={400}
            className={styles.scrollY}
            dataLength={getChildList(list)?.length ?? 0}
          >
            {loading?.child ? (
              <CategoryLoader />
            ) : (
              getChildList(list)?.map((item) => (
                <Flex
                  align="center"
                  justify="space-between"
                  className={cx(styles.category, selected?.child?.id === item.id ? styles.selected : '')}
                  onClick={() => handleItemClick(item, 'child')}
                  key={item.id}
                  {...(selected?.child?.id === item.id ? { ref: childSelectedRef } : {})}
                >
                  <Flex align="center" gap={16}>
                    {item?.icon && (
                      <Image
                        src={item?.icon?.sizes?.document}
                        preview={false}
                        width={32}
                        height={32}
                        placeholder={<Skeleton width={32} height={32} borderRadius={4} />}
                      />
                    )}
                    <div className={styles.categoryName}>{item?.name}</div>
                  </Flex>
                  {!!item?.displayed_children_count && <FaChevronRight size={12} />}
                </Flex>
              ))
            )}
          </InfiniteScroll>
        </div>
        <div className={styles.categoryList}>
          {(selected?.subChild || (selected?.child && !!selected?.child?.displayed_children_count)) && (
            <Input
              placeholder={t('Search')}
              value={subChildSearchQuery}
              onChange={onSubChildInputChange}
              containerClassName={cx('flexGrow', styles.categoryInput)}
            />
          )}
          <InfiniteScroll
            hasMore={!!paginationSubChild?.next_page}
            next={async () => {
              if (!!paginationSubChild?.next_page) {
                const res = await categoriesApi(
                  getQueryParamsForCategoryModal(selected, paginationSubChild?.next_page, subChildSearchQuery)
                );
                const newList = [...list];
                const parentIndex = newList?.findIndex((item) => item?.child?.length);
                const childIndex = newList[parentIndex]?.child?.findIndex((childItem) => childItem?.subChild?.length);
                newList[parentIndex].child[childIndex].subChild = [
                  ...newList[parentIndex].child[childIndex].subChild,
                  ...res?.categories,
                ];
                setList(newList);
                setPaginationSubChild(res?.pagination);
              }
            }}
            loader={selected?.child?.id && <CategoryLoader />}
            height={400}
            className={styles.scrollY}
            dataLength={subChildList?.length ?? 0}
          >
            {(selected?.subChild || (selected?.child && !!selected?.child?.displayed_children_count)) &&
            loading?.subChild ? (
              <CategoryLoader />
            ) : (
              subChildList?.map((item) => (
                <div
                  className={cx(styles.category, selected?.subChild?.id === item.id ? styles.selected : '')}
                  onClick={() => handleItemClick(item, 'sub-child')}
                  key={item.id}
                  {...(selected?.subChild?.id === item.id ? { ref: subChildSelectedRef } : {})}
                >
                  <Flex align="center" gap={16}>
                    {item?.icon && (
                      <Image
                        src={item?.icon?.sizes?.document}
                        preview={false}
                        width={32}
                        height={32}
                        placeholder={<Skeleton width={32} height={32} borderRadius={4} />}
                      />
                    )}
                    <div className={styles.categoryName}>{item?.name}</div>
                  </Flex>
                </div>
              ))
            )}
          </InfiniteScroll>
        </div>
      </Group>
    </>
  );
};

export default CategoryModalBody;
